import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {BehaviorSubject, combineLatest, merge, Observable, of} from 'rxjs';
import {catchError, concatMap, finalize, map, startWith, tap} from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import {
  CartSummary,
  CartSummaryPaymentMethods,
  StoreLocation,
} from '../models/cart-data.model';
import {
  Address,
  AddressType,
  CartOrderAddress,
  CartOrder,
  CartOrderOptions,
  CheckoutData,
  Payments,
  GiftCard,
  SelectAddress,
  ProcessCheckoutResult,
  PaymentType,
  CartOrderProfile,
} from '../models/checkout-data.model';
import { TextValue } from '../models/text-value.model';
import { ApiService } from './api.service';
import { ToastService } from 'ng-uikit-pro-standard';
import { CartParserService } from './cart-parser.service';

export enum ProcessingState {
  Ready,
  PersistingPaymentDataBeforeAuthorization,
  ProcessingAuthorization,
  AuthorizationReceived,
  ChallengeReceived,
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  public isLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public checkingOutState$ = new BehaviorSubject<ProcessingState>(ProcessingState.Ready);

  private _checkingOutState = ProcessingState.Ready;
  public CartSummarySubject = new BehaviorSubject<CartSummary>(null);
  public PaypalPaymentSubject = new BehaviorSubject<Payments>(null);

  private readonly vistekUrl: string;
  private countryProvincesMap = new Map<number, TextValue[]>();
  private _paymentTypeSubject = new BehaviorSubject<PaymentType>(null);
  private checkoutDataSubject = new BehaviorSubject<CheckoutData>(null);
  private storeLocations = [];
  
  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private apiService: ApiService,
    private toaster: ToastService,
    private parser: CartParserService
  ) {
    this.vistekUrl = this.appConfigService.vistekUrl;
  }

  public get checkingOutState(): ProcessingState {
    return this._checkingOutState;
  }
  
  public set checkingOutState(state: ProcessingState) {
    this._checkingOutState = state;
    this.checkingOutState$.next(state);
  }
  
  public get isAnythingLoading$(): Observable<boolean> {
    return this.isLoadingSubject$.pipe(
        startWith(false),
        map(isLoading => {   

        if (isLoading) {
          return true;
        }

        return this.checkingOutState === ProcessingState.PersistingPaymentDataBeforeAuthorization || 
            this.checkingOutState === ProcessingState.ProcessingAuthorization || 
            this.checkingOutState === ProcessingState.ChallengeReceived || 
            this.checkingOutState === ProcessingState.AuthorizationReceived;
      })
    );
  }
  
  public get CheckoutData(): CheckoutData {
    return (window as any).checkoutData;
  }
  
  public get CheckoutDataSubject(): BehaviorSubject<CheckoutData> {
    return this.checkoutDataSubject;
  }
  public get PaymentTypeSubject() {
    return this._paymentTypeSubject;
  }

  private set CheckoutData(checkoutData: CheckoutData) {
    this.parser.initCheckoutData(checkoutData);
    (window as any).checkoutData = checkoutData;
    this.CheckoutDataSubject.next(checkoutData);
  }
  
  public GetProvinces(countryId: number): Observable<TextValue[]> {
    if (this.countryProvincesMap.has(countryId)) {
      return of(this.countryProvincesMap.get(countryId));
    }

    this.setLoading(true);
    
    return this.http
      .get<TextValue[]>(`${this.vistekUrl}/api/cart/checkout/provinces`, { params: { countryId } })
      .pipe(
          finalize(() => {
                this.setLoading(false);
          }),
          tap((list) => this.countryProvincesMap.set(countryId, list))
      );
  }

  public getStores(): Observable<StoreLocation[]> {
    if (this.storeLocations.length > 0) {
      return of(this.storeLocations);
    }

    this.setLoading(true);
    
    return this.http
      .get<StoreLocation[]>(`${this.vistekUrl}/api/cart/checkout/stores`)
      .pipe(
        finalize(() => {
            this.setLoading(false);
        }),
        catchError(() => []),
        tap(stores => this.storeLocations = stores)
      );
  }

  public LoadCheckoutData(force: boolean = false): void {
    let cartData$: Observable<CheckoutData>;
    if (this.CheckoutData && !force) {
      cartData$ = of(this.CheckoutData);
    } else {
      cartData$ = this.http.get<CheckoutData>(`${this.vistekUrl}/cart/checkout`);
    }

    this.setLoading(true);
    
    cartData$.pipe(
      finalize(() => {
          this.setLoading(false);
      })
    ).subscribe((res) => {
      if (res?.CartOrder?.Shipping?.CountryID && res.CartOrder.StateProvList) {
        var textValues = res.CartOrder.StateProvList.map<TextValue>(
          (el) =>
            <TextValue>{
              Text: el.Text,
              Value: el.Value,
            }
        );
        
        this.countryProvincesMap.set(res.CartOrder.Shipping.CountryID, textValues);
      }

      this.CheckoutData = res;
    });
  }
  
  public updatePayment(payment: Payments) {
    
    this.setLoading(true);
    
    return this.http
      .patch<Payments>(`${this.vistekUrl}/cart/checkout/payments`, payment, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
            this.setLoading(false);
        })
      );
  }
  public Process(orderOptions: CartOrderOptions) {
    const formData = new FormData();
    formData.append('Auth', 'false');
    for (var key in orderOptions) {
      if (orderOptions[key] != null) {
        formData.append(key, orderOptions[key]);
      }
    }

    this.setLoading(true);

    return this.http
      .post<ProcessCheckoutResult>(`${this.vistekUrl}/cart/checkout/processcheckout`, formData, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
            this.setLoading(false);
        })
      );
  }

  updateShippingAddress(profile: CartOrderProfile, shippingAddress: CartOrderAddress): Observable<CheckoutData> {
    this.setLoading(true);

    return this.http
      .patch<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/updateProfileInformation`, profile, {
        headers: this.apiService.xsrfHeader,
      }).pipe(
          concatMap(() => this.http
            .patch<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/updateShippingAddress`, shippingAddress, {
              headers: this.apiService.xsrfHeader,
          })
        ), finalize(() => {
              this.setLoading(false);
        }),
        tap((res) => {
          this.CheckoutData = res;
        })
      );
  }

  loadNewShippingRateSelection(shippingCompanyId: string, shippingServiceId: string, requestInsurance: boolean): void {
    const payload = {
        shippingCompanyId,
        shippingServiceId,
        requestInsurance
    };

    this.setLoading(true);

    this.http.post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/shipping/consolidated-rate/select`, payload, {
        headers: this.apiService.xsrfHeader,
      }).pipe(
        finalize(() => {
          this.setLoading(false);
        })).subscribe(x => {
          this.CheckoutData = x;
        });
  }

  selectPickupLocation(locationCode: string): void {
    this.setLoading(true);

    this.http
      .post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/shipping/pickup-location/${locationCode}`, {}, {
        headers: this.apiService.xsrfHeader,
      }).pipe(
        finalize(() => {
          this.setLoading(false);
      })).subscribe(x => {
        this.CheckoutData = x;
      });
  }

  public setShippingMethod(method: string): void {
    this.setLoading(true);

    this.http
      .post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/shipping/method/${method}`, {}, {
        headers: this.apiService.xsrfHeader,
      }).pipe(
        finalize(() => {
          this.setLoading(false);
        })).subscribe(x => {
          this.CheckoutData = x;
        });
  }

  updateBillingAddress(billing: CartOrderAddress) {
    this.setLoading(true);

    return this.http
      .post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/billingaddress`, billing, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
          this.setLoading(false);
        }),
        tap(x => {
          this.CheckoutData = x;
        })
      );
  }

  updateBillingIsShipping(billing: CartOrderAddress) {
    this.setLoading(true);

    return this.http
      .patch<CartOrder>(`${this.vistekUrl}/api/cart/checkout/billingaddress/IsBillingShipping`, billing, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
          this.setLoading(false);
        }),
        tap((res) => {
          this.CheckoutData.CartOrder = res;
        })
      );
  }

  addGiftCard(giftCard: GiftCard): Observable<CheckoutData> {
    this.setLoading(true);

    return this.http
      .post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/giftcard`, giftCard, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
          this.setLoading(false);
        }),
        tap((res) => {
          this.CheckoutData = res;
        })
      );
  }

  removeGiftCard() {
    this.setLoading(true);

    return this.http
      .delete<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/giftcard`, {
        headers: this.apiService.xsrfHeader,
      })
      .pipe(
        finalize(() => {
          this.setLoading(false);
        }),
        tap((res) => {
          this.CheckoutData = res;
        })
      );
  }

  getProfileAddresses(addressType: AddressType): Observable<SelectAddress> {
    this.setLoading(true);
    
    return this.http
        .get<SelectAddress>(`${this.vistekUrl}/api/cart/userprofile/Addresses/${addressType}`)
        .pipe(
            finalize(() => {
              this.setLoading(false);
            })
        );
  }

  useProfileAddress(addressType: AddressType, addressId: string) {
    this.setLoading(true);

    return this.http
      .post<CheckoutData>(
        `${this.vistekUrl}/api/cart/checkout/useAddress`,
        { addressType: addressType, addressId: addressId },
        {
          headers: this.apiService.xsrfHeader,
        }
      )
      .pipe(
        finalize(() => {
          this.setLoading(false);
        }),
        tap((res) => {
          this.CheckoutData = res;
        })
      );
  }


  public updatePromoCode(promoCode: string): Observable<CheckoutData> {
    this.setLoading(true);

    return this.http
        .patch<CheckoutData>(
            `${this.vistekUrl}/api/cart/checkout/promotion`,
            { Code: promoCode },
            { headers: this.apiService.xsrfHeader }
        )
        .pipe(
            finalize(() => {
              this.setLoading(false);
            }),
            tap((res) => {
              this.CheckoutData = res;
            })
        );
  }

  updatePaymentType(newPaymentType: PaymentType): Observable<CheckoutData> {
    this.setLoading(true);
    
    return this.http
        .post<CheckoutData>(`${this.vistekUrl}/api/cart/checkout/payment-type/${newPaymentType}`, null)
        .pipe(
            finalize(() => {
              this.setLoading(false);
            }),
            tap((x) => {
              this.CheckoutData = x;
            })
        );
  }

  mapAddress(address: CartOrderAddress, addressType: AddressType): Address {
    return {
      AddressID: address.AddressID,
      AddressType: addressType,
      PostalCodeZip: address.PostalCode,
      Phone: address.Phone,
      FirstName: address.FirstName,
      LastName: address.LastName,
      Company: address.Company,
      Address_: address.Address,
      Address2: address.Address2,
      CountryID: address.CountryID,
      StateProvinceID: address.StateProvinceID,
      City: address.City,
      CountryName: address.Country,
      StateProvinceName: address.StateProvince,
    };
  }

  isEmailCompleted(){
    return !!this.CheckoutData?.CartOrder.Email && !this.CheckoutData?.CartOrder.LoginRequired;
  }

  showErrorPanel(errors: string[], bottomMsg: string = null): void {
    this.toaster.clear();
    if (!errors?.length) return;
    let liStr = errors
      .map(function (strValue) {
        return `<li>${strValue}</li>`;
      })
      .join('');

    let errorsList = `<ul>${liStr}</ul>`;
    if (bottomMsg) {
      errorsList += `<div>${bottomMsg}</div>`;
    }
    let titleMsg = 'We have checked your cart and found the following:';
    this.toaster.show(errorsList, titleMsg, {
      timeOut: 300000,
      extendedTimeOut: 300000,
      progressBar: true,
      positionClass: 'md-toast-top-full-width',
      closeButton: true,
      enableHtml: true,
      tapToDismiss: true,
      titleClass: 'my-md-toast-title',
      messageClass: 'my-md-toast-message',
      toastClass: 'my-toast',
      opacity: 1.0,
    });
  }
  
  private setLoading(state: boolean): void {
      this.isLoadingSubject$.next(state);
  }
}
